(() => {
  if (customElements.get('wnc-shipping-calculator')) return;

  const stateNameToUf = Object.freeze({
    acre: 'AC', alagoas: 'AL', amapa: 'AP', amazonas: 'AM', bahia: 'BA', ceara: 'CE',
    'distrito federal': 'DF', 'espirito santo': 'ES', goias: 'GO', maranhao: 'MA',
    'mato grosso': 'MT', 'mato grosso do sul': 'MS', 'minas gerais': 'MG', para: 'PA',
    paraiba: 'PB', parana: 'PR', pernambuco: 'PE', piaui: 'PI', 'rio de janeiro': 'RJ',
    'rio grande do norte': 'RN', 'rio grande do sul': 'RS', rondonia: 'RO', roraima: 'RR',
    'santa catarina': 'SC', 'sao paulo': 'SP', sergipe: 'SE', tocantins: 'TO'
  });
  const validUfs = new Set(Object.values(stateNameToUf));
  let geoCache = null;
  let geoPending = null;
  const calendarFormatter = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' });
  const nationalHolidays = new Set(['01-01', '04-21', '05-01', '09-07', '10-12', '11-02', '11-15', '11-20', '12-25']);

  function calendarDay(instant = Date.now()) {
    const parts = Object.fromEntries(calendarFormatter.formatToParts(new Date(instant)).map(part => [part.type, part.value]));
    return new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12));
  }

  function calendarKey(date) {
    return date.toISOString().slice(0, 10);
  }

  function validDays(value) {
    return Number.isSafeInteger(value) && value >= 0 && value <= 365;
  }

  function businessDay(date) {
    const weekday = date.getUTCDay();
    return weekday !== 0 && weekday !== 6 && !nationalHolidays.has(calendarKey(date).slice(5));
  }

  function businessDate(base, days) {
    if (!validDays(days)) return null;
    const date = new Date(base);
    if (days === 0) {
      while (!businessDay(date)) date.setUTCDate(date.getUTCDate() + 1);
      return date;
    }
    for (let remaining = days; remaining > 0;) {
      date.setUTCDate(date.getUTCDate() + 1);
      if (businessDay(date)) remaining -= 1;
    }
    return date;
  }

  function normalizeStateName(value) {
    return typeof value === 'string' ? value.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase().replace(/\s+/g, ' ') : '';
  }

  function startGeoLookup() {
    const pending = { controller: new AbortController(), clients: new Set(), settled: false, cancelled: false };
    geoPending = pending;
    const timer = setTimeout(() => pending.controller.abort(), 5000);
    pending.promise = (async () => {
      let location = null;
      try {
        const response = await fetch('https://get.geojs.io/v1/ip/geo.json', {
          headers: { Accept: 'application/json' },
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          signal: pending.controller.signal
        });
        if (response.ok) {
          const data = await response.json();
          const uf = data.country_code === 'BR' ? stateNameToUf[normalizeStateName(data.region)] : null;
          if (validUfs.has(uf)) location = { uf, city: typeof data.city === 'string' ? data.city.slice(0, 80) : '', source: 'estimated' };
        }
      } catch (_) {
      } finally {
        clearTimeout(timer);
        pending.settled = true;
        if (!pending.cancelled) geoCache = { location, expiresAt: Date.now() + 60000 };
        if (geoPending === pending) geoPending = null;
      }
      return location;
    })();
    return pending;
  }

  class WncShippingCalculator extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true;
      this.controllers = new Set();
      this.unsubscribers = [];
      this.requestVersion = (this.requestVersion || 0) + 1;
      this.cartVersion = (this.cartVersion || 0) + 1;
      this.destinationVersion = (this.destinationVersion || 0) + 1;
      this.scope = this.closest('product-info') || this.closest('.shopify-section') || document;
      try {
        this.config = JSON.parse(this.querySelector('[data-shipping-config]').textContent);
      } catch (_) {
        this.active = false;
        return;
      }
      this.strings = this.config.strings;
      this.allowedServiceCodes = new Set((Array.isArray(this.config.serviceCodes) ? this.config.serviceCodes : []).map(code => String(code).trim()).filter(Boolean));
      this.cart = this.config.cart;
      this.variants = new Map(this.config.variants.map(variant => [String(variant.id), variant]));
      this.cep = this.querySelector('[data-cep]');
      this.submitButton = this.querySelector('[data-submit]');
      this.status = this.querySelector('[data-status]');
      this.results = this.querySelector('[data-results]');
      this.regionLabel = this.querySelector('[data-region-label]');
      this.regionSelect = this.querySelector('[data-region-state]');
      this.regionEdit = this.querySelector('[data-region-edit]');
      this.destination = null;
      this.hasQuote = false;
      this.quoteDay = null;
      this.results.hidden = true;
      this.querySelector('[data-delivery-details]').open = false;
      this.dateLocale = document.documentElement.lang || 'pt-BR';
      this.onCalendarCheck = () => {
        if (this.active && this.hasQuote && this.quoteDay !== calendarKey(calendarDay())) this.invalidate();
      };
      window.addEventListener('focus', this.onCalendarCheck);
      document.addEventListener('visibilitychange', this.onCalendarCheck);
      this.calendarTimer = setInterval(this.onCalendarCheck, 60000);
      this.onSubmit = event => { event.preventDefault(); this.quote(); };
      this.onCepInput = () => {
        this.destinationVersion += 1;
        this.releaseGeoRequest();
        const digits = this.cep.value.replace(/\D/g, '').slice(0, 8);
        this.cep.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
        this.cep.removeAttribute('aria-invalid');
        this.invalidate();
        if (this.destination?.source === 'cep') {
          this.destination = null;
          this.renderDestination();
          this.renderPromotion();
        }
      };
      this.onRegionChange = () => {
        this.destinationVersion += 1;
        this.releaseGeoRequest();
        this.destination = this.regionSelect.value ? { uf: this.regionSelect.value, source: 'manual' } : null;
        this.renderDestination();
        this.renderPromotion();
      };
      this.onSelectionChange = event => {
        const target = event.target;
        if (target instanceof Element && (target.matches('[name="id"]') || target.matches('[name="quantity"]'))) {
          this.invalidate();
          this.renderPromotion();
        }
      };
      this.onCartEvent = event => {
        const cart = event.detail?.cart;
        if (cart && Array.isArray(cart.items) && Number.isFinite(cart.total_price)) {
          this.cartVersion += 1;
          this.cart = cart;
          this.renderPromotion();
        } else {
          this.scheduleCartRefresh();
        }
      };
      this.querySelector('[data-shipping-form]').addEventListener('submit', this.onSubmit);
      this.cep.addEventListener('input', this.onCepInput);
      this.regionSelect.addEventListener('change', this.onRegionChange);
      this.scope.addEventListener('change', this.onSelectionChange);
      document.addEventListener('wnc:cart-updated', this.onCartEvent);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (String(event?.data?.sectionId) !== String(this.config.sectionId)) return;
          const variant = event.data.variant;
          if (variant && this.variants.has(String(variant.id))) {
            const stored = this.variants.get(String(variant.id));
            this.variants.set(String(variant.id), { ...stored, ...variant });
          }
          this.invalidate();
          this.renderPromotion();
        }));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.quantityUpdate, () => {
          this.invalidate();
          this.renderPromotion();
        }));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleCartRefresh()));
      }
      this.renderDestination();
      this.renderPromotion();
      this.refreshCart();
      if (this.config.currency !== 'BRL') {
        this.submitButton.disabled = true;
        this.setStatus(this.strings.currency_unsupported);
        return;
      }
      try {
        const url = new URL(this.config.backendUrl);
        if (url.protocol !== 'https:') throw new Error('url');
        this.backendUrl = url.href.replace(/\/$/, '');
      } catch (_) {
        this.submitButton.disabled = true;
        this.setStatus(this.strings.configuration_pending);
        return;
      }
      this.loadLocation();
    }

    disconnectedCallback() {
      this.active = false;
      this.requestVersion += 1;
      this.cartVersion += 1;
      this.destinationVersion += 1;
      this.releaseGeoRequest();
      this.controllers?.forEach(controller => controller.abort());
      this.unsubscribers?.forEach(unsubscribe => unsubscribe());
      clearTimeout(this.cartTimer);
      clearInterval(this.calendarTimer);
      window.removeEventListener('focus', this.onCalendarCheck);
      document.removeEventListener('visibilitychange', this.onCalendarCheck);
      this.querySelector('[data-shipping-form]')?.removeEventListener('submit', this.onSubmit);
      this.cep?.removeEventListener('input', this.onCepInput);
      this.regionSelect?.removeEventListener('change', this.onRegionChange);
      this.scope?.removeEventListener('change', this.onSelectionChange);
      document.removeEventListener('wnc:cart-updated', this.onCartEvent);
    }

    format(key, values = {}) {
      return (this.strings[key] || '').replace(/\{([a-z_]+)\}/g, (_, name) => String(values[name] ?? ''));
    }

    renderText(target, key, values = {}, emphasized = []) {
      const template = this.strings[key] || '';
      const nodes = [];
      let offset = 0;
      for (const match of template.matchAll(/\{([a-z_]+)\}/g)) {
        nodes.push(document.createTextNode(template.slice(offset, match.index)));
        const value = String(values[match[1]] ?? '');
        if (emphasized.includes(match[1])) {
          const strong = document.createElement('strong');
          strong.textContent = value;
          nodes.push(strong);
        } else {
          nodes.push(document.createTextNode(value));
        }
        offset = match.index + match[0].length;
      }
      nodes.push(document.createTextNode(template.slice(offset)));
      target.replaceChildren(...nodes);
    }

    emphasizeText(target, value) {
      const strong = document.createElement('strong');
      strong.textContent = value;
      target.replaceChildren(strong);
    }

    money(cents) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
    }

    selection() {
      const form = document.getElementById(this.config.formId);
      const variantInput = form?.querySelector('[name="id"]');
      const variantId = variantInput ? variantInput.value : this.config.selectedVariantId;
      const variant = this.variants.get(String(variantId));
      if (!variant || !variant.available || variantInput?.disabled) return null;
      const quantityInput = form?.elements.namedItem('quantity') || this.scope.querySelector('[name="quantity"]');
      const quantity = Number(quantityInput?.value || 1);
      if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 100) return { variant, quantity: null };
      return { variant, quantity };
    }

    subtotal() {
      const selection = this.selection();
      const cartTotal = Number(this.cart?.total_price) || 0;
      if (!selection?.quantity) return cartTotal;
      const quantityInCart = (this.cart?.items || []).reduce((quantity, item) =>
        String(item.variant_id || item.id) === String(selection.variant.id) ? quantity + Number(item.quantity || 0) : quantity, 0);
      const additionalQuantity = Math.max(0, selection.quantity - quantityInCart);
      return Math.max(0, cartTotal + additionalQuantity * selection.variant.price);
    }

    renderPromotion() {
      const promotion = this.querySelector('[data-promotion]');
      const text = this.querySelector('[data-promotion-text]');
      const progress = this.querySelector('[data-progress]');
      const note = this.querySelector('[data-promotion-note]');
      const uf = this.destination?.uf;
      const eligible = ['PR', 'SC', 'RS', 'SP', 'RJ', 'MG', 'ES'].includes(uf);
      promotion.hidden = Boolean(uf && !eligible) || this.config.currency !== 'BRL';
      progress.hidden = !eligible;
      note.hidden = !eligible;
      if (!eligible) {
        text.textContent = this.strings.rule;
        return;
      }
      const subtotal = this.subtotal();
      const remaining = Math.max(0, 39901 - subtotal);
      if (remaining > 0) this.renderText(text, 'remaining', { amount: this.money(remaining) }, ['amount']);
      else this.emphasizeText(text, this.strings.reached);
      const value = Math.min(39900, subtotal);
      progress.setAttribute('aria-valuenow', String(value / 100));
      progress.setAttribute('aria-valuetext', text.textContent);
      this.querySelector('[data-progress-fill]').style.width = `${Math.min(100, subtotal / 399)}%`;
    }

    renderDestination() {
      const { uf, city, source } = this.destination || {};
      const validUf = validUfs.has(uf);
      if (!validUf) this.destination = null;
      this.regionLabel.textContent = validUf ? this.format(source === 'cep' ? 'cep_region' : source === 'manual' ? 'manual_region' : 'estimated_region', { region: city ? `${city} — ${uf}` : uf }) : this.strings.unknown_region;
      this.regionSelect.value = validUf ? uf : '';
      this.regionEdit.hidden = validUf && source === 'cep';
      if (this.regionEdit.hidden) this.regionEdit.open = false;
    }

    setStatus(message = '', error = false) {
      this.status.textContent = message;
      this.status.dataset.error = String(error);
    }

    invalidate() {
      this.requestVersion += 1;
      this.quoteController?.abort();
      this.regionController?.abort();
      const hadQuote = this.hasQuote || this.busy;
      this.hasQuote = false;
      this.quoteDay = null;
      this.results.hidden = true;
      this.querySelector('[data-delivery-details]').open = false;
      this.setBusy(false);
      if (hadQuote) this.setStatus(this.strings.quote_changed);
    }

    setBusy(busy) {
      this.busy = busy;
      this.submitButton.disabled = busy || !this.backendUrl || this.config.currency !== 'BRL';
      this.submitButton.textContent = busy ? this.strings.calculating : this.strings.calculate;
      this.querySelector('[data-shipping-form]').setAttribute('aria-busy', String(busy));
    }

    async request(path, body, controller = new AbortController()) {
      this.controllers.add(controller);
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(`${this.backendUrl}${path}`, {
          method: body ? 'POST' : 'GET',
          headers: body ? { 'Content-Type': 'application/json', 'Accept': 'application/json' } : { 'Accept': 'application/json' },
          body: body ? JSON.stringify(body) : undefined,
          credentials: 'omit',
          signal: controller.signal
        });
        if (!response.ok) throw new Error('request');
        const data = await response.json();
        if (data.ok === false) throw new Error('request');
        return data;
      } finally {
        clearTimeout(timer);
        this.controllers.delete(controller);
      }
    }

    async loadLocation() {
      const version = this.destinationVersion;
      let location = null;
      try {
        const data = await this.request('/api/shipping/location');
        if (validUfs.has(data.uf)) location = { uf: data.uf, city: data.city, source: 'estimated' };
      } catch (_) {}
      if (!this.canUseLocation(version)) return;
      if (!location) location = await this.browserLocation();
      if (!this.canUseLocation(version)) return;
      this.destination = location;
      this.renderDestination();
      this.renderPromotion();
    }

    canUseLocation(version) {
      return this.active && version === this.destinationVersion && !this.cep.value && this.destination?.source !== 'manual' && this.destination?.source !== 'cep';
    }

    async browserLocation() {
      if (geoCache && geoCache.expiresAt > Date.now()) return geoCache.location;
      const pending = geoPending && !geoPending.controller.signal.aborted ? geoPending : startGeoLookup();
      pending.clients.add(this);
      this.geoRequest = pending;
      try {
        return await pending.promise;
      } finally {
        if (this.geoRequest === pending) this.releaseGeoRequest();
      }
    }

    releaseGeoRequest() {
      const pending = this.geoRequest;
      if (!pending) return;
      this.geoRequest = null;
      pending.clients.delete(this);
      if (!pending.settled && pending.clients.size === 0) {
        pending.cancelled = true;
        pending.controller.abort();
      }
    }

    scheduleCartRefresh() {
      clearTimeout(this.cartTimer);
      this.cartTimer = setTimeout(() => this.refreshCart(), 60);
    }

    async refreshCart() {
      const version = ++this.cartVersion;
      const controller = new AbortController();
      this.controllers.add(controller);
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) return;
        const cart = await response.json();
        if (!this.active || version !== this.cartVersion || !Array.isArray(cart.items) || !Number.isFinite(cart.total_price)) return;
        this.cart = cart;
        this.renderPromotion();
      } catch (_) {
      } finally {
        this.controllers.delete(controller);
      }
    }

    async quote() {
      if (!this.backendUrl || this.busy) return;
      const recipientCep = this.cep.value.replace(/\D/g, '');
      if (recipientCep.length !== 8) {
        this.cep.setAttribute('aria-invalid', 'true');
        this.setStatus(this.strings.invalid_cep, true);
        this.cep.focus();
        return;
      }
      const selection = this.selection();
      if (!selection) { this.setStatus(this.strings.invalid_variant, true); return; }
      if (!selection.quantity) { this.setStatus(this.strings.invalid_quantity, true); return; }
      this.invalidate();
      const version = this.requestVersion;
      this.quoteController = new AbortController();
      this.regionController = new AbortController();
      this.setBusy(true);
      this.setStatus(this.strings.loading);
      const weightGrams = this.config.weightMode === 'default' ? this.config.fallbackWeightGrams : selection.variant.weight > 0 ? selection.variant.weight : this.config.fallbackWeightGrams;
      const regionPromise = this.request('/api/shipping/region', { cep: recipientCep, subtotalCents: this.subtotal() }, this.regionController)
        .then(data => {
          if (!this.active || version !== this.requestVersion) return;
          this.destination = data.uf ? { uf: data.uf, city: data.city, source: 'cep' } : null;
          this.renderDestination();
          this.renderPromotion();
        }).catch(() => {
          if (!this.active || version !== this.requestVersion) return;
          this.destination = null;
          this.renderDestination();
          this.renderPromotion();
        });
      try {
        const data = await this.request('/api/shipping/quote', {
          recipientCep,
          items: [{ sku: selection.variant.sku || String(selection.variant.id), quantity: selection.quantity, weightGrams, ...this.config.dimensions, unitPriceCents: selection.variant.price }]
        }, this.quoteController);
        if (!this.active || version !== this.requestVersion) return;
        const options = (Array.isArray(data.options) ? data.options : []).filter(option => Number.isFinite(option.priceCents) && option.priceCents >= 0 && (!this.allowedServiceCodes.size || this.allowedServiceCodes.has(String(option.serviceCode)) || this.isPickup(option)))
          .sort((a, b) => a.priceCents - b.priceCents || (a.deliveryDays ?? Infinity) - (b.deliveryDays ?? Infinity));
        if (data.noOptions || !options.length) {
          this.setStatus(this.strings.no_options);
          return;
        }
        const receivedAt = Date.now();
        const reference = this.quoteReference(data, receivedAt);
        if (calendarKey(reference) !== calendarKey(calendarDay(receivedAt))) {
          this.setStatus(this.strings.quote_changed);
          return;
        }
        this.renderQuote(options, data, selection.quantity, reference);
        this.hasQuote = true;
        this.results.hidden = false;
        this.setStatus();
      } catch (_) {
        if (this.active && version === this.requestVersion) this.setStatus(this.strings.network_error, true);
      } finally {
        await regionPromise;
        if (this.active && version === this.requestVersion) this.setBusy(false);
      }
    }

    quoteReference(data, receivedAt = Date.now()) {
      const timestamp = typeof data.quotedAt === 'string' && /^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(data.quotedAt) ? Date.parse(data.quotedAt) : NaN;
      return calendarDay(Number.isFinite(timestamp) ? timestamp : receivedAt);
    }

    dateText(date, short = false, includeYear = false) {
      const options = { timeZone: 'UTC', day: short ? '2-digit' : 'numeric', month: short ? '2-digit' : 'long' };
      if (includeYear) options.year = 'numeric';
      try {
        return new Intl.DateTimeFormat(this.dateLocale, options).format(date);
      } catch (_) {
        return new Intl.DateTimeFormat('pt-BR', options).format(date);
      }
    }

    arrival(option, reference) {
      const end = businessDate(reference, option.deliveryDays);
      if (!end) return { label: this.strings.unknown_days, short: this.strings.unavailable, dispatch: null };
      const originalValid = validDays(option.originalDeliveryDays) && option.originalDeliveryDays <= option.deliveryDays;
      const additionalValid = originalValid && validDays(option.additionalDays) && option.originalDeliveryDays + option.additionalDays === option.deliveryDays;
      const dispatch = additionalValid ? businessDate(reference, option.additionalDays) : null;
      const includeYear = reference.getUTCFullYear() !== end.getUTCFullYear();
      const values = { date: this.dateText(end, false, includeYear) };
      return { key: 'arrives_by', values, label: this.format('arrives_by', values), short: this.format('date_by', { date: this.dateText(end, true, includeYear) }), dispatch, additionalDays: additionalValid ? option.additionalDays : null, includeYear, window: false };
    }

    isPickup(option) {
      return /(retirada|pickup)/i.test(`${option.service || ''} ${option.carrier || ''}`);
    }

    renderQuote(options, data, quantity, reference = this.quoteReference(data)) {
      this.quoteDay = calendarKey(reference);
      const deliveries = options.filter(option => !this.isPickup(option));
      const pickups = options.filter(option => this.isPickup(option));
      const featured = deliveries[0];
      const arrival = featured ? this.arrival(featured, reference) : null;
      const arrivalText = this.querySelector('[data-arrival]');
      if (arrival?.key) {
        this.renderText(arrivalText, arrival.key, arrival.values, ['date', 'start', 'end']);
      } else {
        arrivalText.textContent = arrival ? arrival.label : this.strings.pickup_only;
      }
      const priceText = this.querySelector('[data-featured-price]');
      if (!featured) priceText.textContent = this.strings.pickup_options;
      else if (featured.priceCents === 0) this.emphasizeText(priceText, this.strings.free_price);
      else this.renderText(priceText, 'featured_price', { price: this.money(featured.priceCents) }, ['price']);
      this.querySelector('[data-scope]').textContent = quantity === 1 ? this.strings.single_scope : this.format('scope', { quantity });
      this.querySelector('[data-timeline]').hidden = !featured;
      this.querySelector('[data-timeline-arrival]').textContent = arrival?.short || '';
      this.querySelector('[data-timeline-dispatch]').textContent = arrival?.dispatch ? this.format('date_by', { date: this.dateText(arrival.dispatch, true, arrival.includeYear) }) : this.strings.dispatch_unknown;
      this.querySelector('[data-preparation-note]').textContent = arrival && arrival.additionalDays !== null && arrival.additionalDays !== undefined ? this.format(arrival.additionalDays === 1 ? 'preparation_note_one' : 'preparation_note', { days: arrival.additionalDays }) : this.strings.preparation_unknown;
      this.querySelector('[data-delivery-note]').textContent = arrival?.window ? this.strings.delivery_window_note : this.strings.delivery_note;
      const list = this.querySelector('[data-options]');
      list.replaceChildren();
      [...deliveries, ...pickups].forEach(option => {
        const pickup = this.isPickup(option);
        const row = document.createElement('li');
        row.className = 'wnc-shipping__option';
        const description = document.createElement('div');
        const name = document.createElement('strong');
        name.textContent = option.service || option.carrier || this.strings.unavailable;
        const detail = document.createElement('span');
        detail.className = 'wnc-shipping__option-meta';
        detail.textContent = pickup ? validDays(option.deliveryDays) ? this.format('pickup_days', { days: option.deliveryDays }) : this.strings.pickup_label : this.arrival(option, reference).short;
        const price = document.createElement('span');
        price.className = 'wnc-shipping__option-price';
        price.textContent = option.priceCents === 0 ? pickup ? this.strings.pickup_free : this.strings.free_price : this.money(option.priceCents);
        description.append(name, detail);
        row.append(description, price);
        list.append(row);
      });
    }
  }

  customElements.define('wnc-shipping-calculator', WncShippingCalculator);
})();
